define([
    "dojo/_base/declare",
    "dojo/_base/event",
    "dojo/_base/lang",
    "dojo/_base/window",
    "dojo/dom",
    "dojo/dom-attr",
    "dojo/dom-class",
    "dojo/dom-construct",
    "dojo/dom-geometry",
    "dojo/dom-style",
    "dojo/keys",
    "dojo/mouse",
    "dojo/on",
    "dojo/touch",
    "dijit/_TemplatedMixin",
    "dijit/_WidgetBase",
    "MW/layout/ActionsMenu",
    "MW/layout/LayoutChildProperty",
    "MW/layout/LayoutUtils",
    "MW/layout/Tab",
    "mw-form/ContextMenu",
    "dojo/i18n!l10n/nls/layout",
    "dojo/text!./templates/TitleBar.html"
], function (declare, event, lang, window, dom, domAttr, domClass, domConstruct, domGeometry, domStyle, keys, mouse, on,
             touch, _TemplatedMixin, _WidgetBase, ActionsMenu, LayoutChildProperty, LayoutUtils, Tab, ContextMenu, layoutL10n,
             titleBarTemplate) {

    /**
     * Defines a title bar for use within various layout containers
     */
    var TitleBar = declare([Tab, _TemplatedMixin], {

        // Optional properties passed to constructor
        // properties - used to obtain and track title and collapsed state
        // isCollapsible - title bar will display expand/collapse affordance if true
        // isMaximizable - title bar will set maximize action in response to double click

        templateString: titleBarTemplate,

        buildRendering: function () {
            this.inherited(arguments);

            dom.setSelectable(this.domNode, false);
            LayoutUtils.preventFocus(this.domNode, true);
            LayoutUtils.setLocaleOnDomNode(this.domNode);

            var that = this;
            this._watches = [];
            if (this.properties) {
                this.set(LayoutChildProperty.TITLE, this.properties.title);
                this._watches.push(this.properties.watch(LayoutChildProperty.TITLE, function (name, oldValue, newValue) {
                    that.set(LayoutChildProperty.TITLE, newValue);
                }));

                this.set("toolTip", this.properties.description);
                this._watches.push(this.properties.watch(LayoutChildProperty.DESCRIPTION, function (name, oldValue, newValue) {
                    that.set("toolTip", newValue);
                }));

                this.set(LayoutChildProperty.IS_COLLAPSED, this.properties.isCollapsed);
                this._watches.push(this.properties.watch(LayoutChildProperty.IS_COLLAPSED, function (name, oldValue, newValue) {
                    that.set(LayoutChildProperty.IS_COLLAPSED, newValue)
                }));

                this.set(LayoutChildProperty.ACTIONS, this.properties.actions);
                this._watches.push(this.properties.watch(LayoutChildProperty.CONEXT_ACTIONS, function (name, oldValue, newValue) {
                    that.set(LayoutChildProperty.ACTIONS, newValue);
                }));

                if (this.properties.get(LayoutChildProperty.IS_SELECTED)) {
                    domClass.add(that.domNode, "selected");
                }
                this._watches.push(this.properties.watch(LayoutChildProperty.IS_SELECTED, function (name, oldValue, newValue) {
                    if (!that.domNode) {
                        return;  // Happens on IE for some reason
                    }
                    if (newValue) {
                        domClass.add(that.domNode, "selected");
                    } else {
                        domClass.remove(that.domNode, "selected");
                    }
                }));

                if (this.properties.get(LayoutChildProperty.IS_MAXIMIZABLE)) {
                    this._maximizeRestoreAction = {
                        id: "maximizeRestore",
                        enabled: true,
                        text: "",  // Will update before showing
                        callback: function (e) {
                            if (e.mwEventData && e.mwEventData.details
                                && (e.mwEventData.details.keyCode === keys.ENTER || e.mwEventData.details.keyCode === keys.SPACE)) {
                                const parent = that.getParent ? that.getParent() : null;
                                if (parent) {
                                    // Set the flag on the AccordionContainer to block next child selection since this is a keyboard interaction.
                                    parent._shouldBlockSuccession = true;
                                }
                            }
                            that.properties.set(LayoutChildProperty.IS_MAXIMIZED, !that.properties.get(LayoutChildProperty.IS_MAXIMIZED));
                        }
                    };
                }

                if (this.properties.get(LayoutChildProperty.IS_COLLAPSIBLE) !== false) {
                    this._collapseExpandAction = {
                        id: "collapseExpand",
                        enabled: true,
                        text: "",  // Will update before showing
                        callback: function (e) {
                            if (e.mwEventData && e.mwEventData.details
                                && (e.mwEventData.details.keyCode === keys.ENTER || e.mwEventData.details.keyCode === keys.SPACE)) {
                                const parent = that.getParent ? that.getParent() : null;
                                if (parent) {
                                    // Set the flag on the AccordionContainer to block next child selection since this is a keyboard interaction.
                                    parent._shouldBlockSuccession = true;
                                }
                            }
                            that.properties.set(LayoutChildProperty.IS_COLLAPSED, !that.properties.get(LayoutChildProperty.IS_COLLAPSED));
                        }
                    };
                }

                if (this.properties.get("closable")) {
                    this._closeAction = {
                        id: "close",
                        enabled: true,
                        text: layoutL10n.close,
                        callback: function () {
                            that.properties.set(LayoutChildProperty.IS_OPEN, false);
                        }
                    };
                }

                this.own(on(this.domNode, touch.press, function (e) {
                    if (that.get(LayoutChildProperty.IS_COLLAPSED) || e.target === that.toggleNode) {
                        that.isArmed = false;  // To prevent select on release
                    } else if (that.properties.isSelectable !== false) {
                        that.properties.set(LayoutChildProperty.IS_SELECTED, true);
                    }
                    LayoutUtils.stopEvent(e)
                }));

                if (this.properties.get(LayoutChildProperty.IS_MAXIMIZABLE)) {
                    this.own(on(this.domNode, "dblclick", function (e) {
                        that.properties.set(LayoutChildProperty.IS_MAXIMIZED, !that.properties.get(LayoutChildProperty.IS_MAXIMIZED));
                    }));
                }

            } else {
                this._setTitleAttr(this.title);
                this._setToolTipAttr(this.description);
                this._setIsCollapsedAttr(this.isCollapsed);
                this._setActionsAttr(this.actions);
            }
            if (this.title) {
                domAttr.set(this.actionsNodeWrapper, "title", lang.replace(layoutL10n.childActions, {placeholder: this.title}));
            }
            this.own(on(this.actionsNode, touch.press, this._touchPressHandler.bind(this)));
            this.own(on(this.actionsNodeWrapper, "keydown", this._keydownHandler.bind(this)));
            this.own(on(this.actionsNodeWrapper, "keyup", this._keyupHandler.bind(this)));
        },

        _touchPressHandler: function (e) {
            if (mouse.isLeft(e)) {
                this._displayActionsMenu(e);
            }
        },

        _keydownHandler: function (e) {
            if (e.keyCode === keys.ENTER) {
                this._displayActionsMenu(e);
            }
        },

        _keyupHandler: function (e) {
            if (e.keyCode === keys.SPACE) {
                this._displayActionsMenu(e);
            }
        },

        destroy: function () {
            this._watches.forEach(function (watch) {
                watch.remove();
            });
            if (this._contextMenu) {
                this._contextMenu.destroyRecursive();
            }
            this.inherited(arguments);
        },

        _setTitleAttr: function (title) {
            this.titleNode.textContent = title;
            this._set("title", title);
        },

        _getTitleAttr: function () {
            return this.titleNode.textContent;
        },

        _setToolTipAttr: function (toolTip) {
            if (toolTip) {
                domAttr.set(this.titleNode, "title", toolTip);
            } else {
                domAttr.remove(this.titleNode, "title");
            }
            this._set("toolTip", toolTip);
        },

        _getToolTipAttr: function () {
            return domAttr.get(this.titleNode, "title");
        },

        _setIsCollapsedAttr: function (isCollapsed) {
            if (this.isCollapsible) {
                if (isCollapsed) {
                    domClass.replace(this.toggleNode, "expandIcon", "collapseIcon");
                } else {
                    domClass.replace(this.toggleNode, "collapseIcon", "expandIcon");
                }
            }
            this._set(LayoutChildProperty.IS_COLLAPSED, isCollapsed);
        },

        _setIsCollapsibleAttr: function (isCollapsible) {
            this._set(LayoutChildProperty.IS_COLLAPSIBLE, isCollapsible);
            if (!isCollapsible) {
                domClass.remove(this.toggleNode, "expandIcon");
                domClass.remove(this.toggleNode, "collapseIcon");
                domStyle.set(this.titleNode, {left: "0"});
            } else {
                var saveIsCollapsed = this.isCollapsed;
                delete this.isCollapsed;
                this.set("isCollapsed", saveIsCollapsed);
                domStyle.set(this.titleNode, {left: "16px"});
            }
            if (!this._hasContextActions()) {
                this._updateActionsDropDown();
                this._updateContextMenu();
            }
        },

        _setIsMaximizableAttr: function (isMaximizable) {
            this._set(LayoutChildProperty.IS_MAXIMIZABLE, isMaximizable);
            if (!this._hasContextActions()) {
                this._updateActionsDropDown();
                this._updateContextMenu();
            }
        },

        _setActionsAttr: function (actions) {
            // Update the actions button visibility accordingly
            this._set(LayoutChildProperty.ACTIONS, actions);
            this._updateActionsDropDown();
            this._updateContextMenu();
        },

        _displayActionsMenu: function (e) {
            ActionsMenu.display(this.actionsNode, this._getAllActions());
            event.stop(e);
            this.actionsNodeWrapper.setAttribute("aria-expanded", "true");
        },

        _updateActionsDropDown: function() {
            if (this._hasAnyActions()) {
                this.actionsNodeWrapper.style.visibility = "visible";
                this.actionsNodeWrapper.setAttribute("tabindex", -1);
            } else {
                this.actionsNodeWrapper.style.visibility = "hidden";
                this.actionsNodeWrapper.removeAttribute("tabindex");
            }
        },

        _updateContextMenu: function () {
            var that = this;
            if (this._hasAnyActions()) {
                if (!this._contextMenu) {
                    this._contextMenu = new ContextMenu({
                        id: this.id + "ContextMenu",
                        targetNodes: [this.domNode, "#" + this.id + " *"]
                    });
                    this._contextMenu.willOpenCallback = function () {
                        that._contextMenu.getChildren().forEach(function(child) {
                            child.destroyRecursive();
                        });
                        ActionsMenu._addMenuItems(that._contextMenu, that._getAllActions());
                    };
                }
            } else {
                if (this._contextMenu) {
                    this._contextMenu.destroyRecursive();
                }
                LayoutUtils.preventDefaultContextMenu(this.domNode);
            }
        },

        _hasAnyActions: function () {
            return this._hasContextActions() || this._hasStandardActionsOtherThanCollapse();
        },

        _hasContextActions: function () {
            return this.actions && this.actions.length > 0;
        },

        _hasStandardActions: function () {
            return (this.isMaximizable && this._maximizeRestoreAction) ||
                   (this.isCollapsible && this._collapseExpandAction) ||
                   this._closeAction ||
                   (this.properties && this.properties._globalActionsSupplier);
        },

        _hasStandardActionsOtherThanCollapse: function () {
            // This function is used to avoid showing a context/action menu containing only collapse/expand
            // since there is already a collapse/expand affordance.
            return (this.isMaximizable && this._maximizeRestoreAction) ||
                   this._closeAction;
        },

        _getAllActions: function () {
            this._updateStandardActionLabels();
            var actions;
            if (this._hasContextActions()) {
                actions = this.actions.slice();
            } else {
                actions = [];
            }
            if (this._hasStandardActions()) {
                if (actions.length > 0) {
                    actions.push(null);
                }
                if (this.isMaximizable && this._maximizeRestoreAction) {
                    actions.push(this._maximizeRestoreAction);
                }
                if (this.isCollapsible && this._collapseExpandAction) {
                    actions.push(this._collapseExpandAction);
                }
                if (this.properties && this.properties._globalActionsSupplier) {
                    actions = actions.concat(this.properties._globalActionsSupplier.getActions());
                }
                if (this._closeAction) {
                    actions.push(this._closeAction);
                }
            }
            return actions;
        },

        _updateStandardActionLabels: function () {
            if (this._maximizeRestoreAction) {
                this._maximizeRestoreAction.text = this.properties.get(LayoutChildProperty.IS_MAXIMIZED) ? layoutL10n.restore : layoutL10n.maximize;
            }
            if (this._collapseExpandAction) {
                this._collapseExpandAction.text = this.properties.get(LayoutChildProperty.IS_COLLAPSED) ? layoutL10n.expand : layoutL10n.collapse;
            }
        }
    });

    TitleBar.getPreferredHeight = function () {
        if (!TitleBar._preferredHeight) {
            // Create a dummy title bar, temporarily add it to the document, measure it,
            // remove it and finally destroy it.
            var bar = new TitleBar({id: "dummyTitleBar", title: "Dummy Title"});
            domStyle.set(bar.toggleNode, {position: "static"});
            domStyle.set(bar.titleNode, {position: "static"});
            domStyle.set(bar.actionsNode, {position: "static"});
            domConstruct.place(bar.domNode, window.body());
            domStyle.set(bar.domNode, {visibility: "hidden"});
            this._preferredHeight = domGeometry.getMarginBox(bar.domNode).h;
            window.body().removeChild(bar.domNode);
            bar.destroy();
        }
        return TitleBar._preferredHeight;
    };

    return TitleBar;
});
